import { isArray } from "lodash-es";
import type { LocationQuery, LocationQueryRaw, LocationQueryValue } from "vue-router";

import type { UnknownRecord } from "../type";

type QueryValue = LocationQueryValue | LocationQueryValue[];

export function firstQueryParam(query: LocationQuery, name: string) {
  return firstQueryValue(query[name]);
}

export function serializeObject(obj: UnknownRecord, prefix: string): LocationQuery {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [prefix + key, serialize(value)]),
  );
}

export function deserializeObject(query: LocationQuery, prefix: string) {
  return Object.fromEntries(
    Object.entries(query).flatMap(([key, value]) =>
      key.startsWith(prefix)
        ? [[key.substring(prefix.length), deserialize(firstQueryValue(value))]]
        : [],
    ),
  );
}

export function removePropsWithPrefix(query: LocationQueryRaw, prefix: string) {
  return Object.fromEntries(
    Object.entries(query).flatMap(([key, value]) => (key.startsWith(prefix) ? [] : [[key, value]])),
  );
}

function firstQueryValue(value?: QueryValue): LocationQueryValue | undefined {
  return isArray(value) ? value[0] : value;
}

function serialize(value: unknown): LocationQueryValue {
  if (value === null || value === undefined) {
    return null;
  }

  if (isArray(value) && value.every((v) => typeof v === "string" || typeof v === "number")) {
    return "[" + value.join() + "]";
  }

  if (typeof value === "string") return value;
  if (typeof value === "boolean" || typeof value === "number") {
    return String(value);
  }

  return "__json__" + encodeURIComponent(JSON.stringify(value));
}

function deserialize(value?: LocationQueryValue): QueryValue {
  if (!value) {
    return null;
  }

  if (value.startsWith("__json__")) {
    return JSON.parse(decodeURIComponent(value.slice(8))) as QueryValue;
  }

  if (value.startsWith("[") && value.endsWith("]")) {
    return value.substring(1, value.length - 1).split(",");
  }

  return value;
}
